import * as React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Chip, IconButton, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { ArrowDownIcon } from "@phosphor-icons/react/dist/ssr/ArrowDown";
import { ArrowUpIcon } from "@phosphor-icons/react/dist/ssr/ArrowUp";
import { DotsSixVerticalIcon } from "@phosphor-icons/react/dist/ssr/DotsSixVertical";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/ssr/UploadSimple";

import type { Voice } from "@/hooks/api/use-voices";
import { UPLOAD_PROVIDER_KEY, VOICE_SAMPLE_ACCEPT } from "@/hooks/api/use-voices";
import { VoiceSampleButton } from "@/components/voice-sample-button";

export interface VoiceCardProps {
	voice: Voice;
	/** Where it sits in its own language's list, and how long that list is — the arrows need both. */
	index: number;
	count: number;
	busy: boolean;
	onMove: (voice: Voice, offset: -1 | 1) => void;
	onToggleActive: (voice: Voice, isActive: boolean) => void;
	onUploadSample: (voice: Voice, file: File) => void;
	onEdit: (voice: Voice) => void;
	onRemove: (voice: Voice) => void;
}

/**
 * One curated voice, as a card rather than a table row.
 *
 * Four or five per language is a set to look at, not a grid to scan, and half of what matters
 * about a voice — how it sounds — is a button rather than a column. The provider and its voice id
 * are here because this is the screen where somebody has to check them against the provider's
 * dashboard; they are muted, and they never leave this screen.
 */
export function VoiceCard({
	voice,
	index,
	count,
	busy,
	onMove,
	onToggleActive,
	onUploadSample,
	onEdit,
	onRemove,
}: VoiceCardProps): React.JSX.Element {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: voice.id });

	const descriptors = [voice.persona, voice.gender].filter(Boolean).join(" · ");

	/** Where this voice comes from: a vendor and its id, or the fact that it is a recording. */
	const origin =
		voice.providerPluginKey === UPLOAD_PROVIDER_KEY
			? "Uploaded recording"
			: [voice.providerPluginKey, voice.providerVoiceId].filter(Boolean).join(" · ");

	return (
		<Stack
			ref={setNodeRef}
			direction="row"
			spacing={1}
			// Through `style` rather than `sx`: these two change on every frame of a drag, and each
			// new value through `sx` mints and injects another emotion class.
			style={{ transform: CSS.Transform.toString(transform), transition }}
			sx={{
				alignItems: "center",
				p: 1.5,
				position: "relative",
				zIndex: isDragging ? 1 : 0,
				// On the dark ground a dragged row has to lift off the card behind it, and opacity
				// alone reads as "disabled" rather than "moving".
				bgcolor: isDragging ? "action.hover" : undefined,
				opacity: voice.isActive ? 1 : 0.72,
			}}
		>
			{/* The only voice in its language has no order to be in: a grab handle that cannot move it
			    and two arrows that are disabled for as long as the list is one long are three controls
			    with one answer between them. */}
			{count > 1 ? (
				<Stack direction="row" spacing={0.25} sx={{ alignItems: "center", flex: "0 0 auto" }}>
					<Tooltip title="Drag to reorder">
						<IconButton size="small" sx={{ cursor: "grab", touchAction: "none" }} {...attributes} {...listeners}>
							<DotsSixVerticalIcon fontSize="var(--icon-fontSize-md)" />
						</IconButton>
					</Tooltip>

					<Stack sx={{ flex: "0 0 auto" }}>
						<IconButton
							aria-label="Move up"
							size="small"
							disabled={busy || index === 0}
							onClick={() => onMove(voice, -1)}
							sx={{ p: 0.25 }}
						>
							<ArrowUpIcon fontSize="var(--icon-fontSize-sm)" />
						</IconButton>
						<IconButton
							aria-label="Move down"
							size="small"
							disabled={busy || index === count - 1}
							onClick={() => onMove(voice, 1)}
							sx={{ p: 0.25 }}
						>
							<ArrowDownIcon fontSize="var(--icon-fontSize-sm)" />
						</IconButton>
					</Stack>
				</Stack>
			) : null}

			{/* Keyed on the row's own timestamp: the sample URL is the same string after a sample is
			    replaced, so without this the button would play the bytes it cached for the
			    previous one. */}
			<VoiceSampleButton key={voice.updatedAt} sampleUrl={voice.sampleUrl} name={voice.displayName} />

			<Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
					<Typography variant="subtitle2" noWrap>
						{voice.displayName}
					</Typography>
					{voice.isActive ? null : <Chip label="Retired" size="small" variant="outlined" />}
					{voice.hasSample ? null : <Chip color="warning" label="No sample" size="small" variant="soft" />}
				</Stack>

				{descriptors ? (
					<Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
						{descriptors}
					</Typography>
				) : null}

				{/* An uploaded voice has no id to show and no vendor to check it against — the line is
				    the provider alone rather than a provider and a trailing separator. */}
				<Typography variant="caption" color="text.disabled" noWrap title={origin} sx={{ display: "block" }}>
					{origin}
				</Typography>
			</Box>

			<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flex: "0 0 auto" }}>
				<Tooltip title={voice.isActive ? "On the list" : "Off the list"}>
					<Switch
						checked={voice.isActive}
						disabled={busy}
						size="small"
						// The tooltip names the Switch's root span; the checkbox inside it is what a
						// screen reader reaches, and it would otherwise announce a bare "checkbox".
						slotProps={{ input: { "aria-label": `${voice.displayName} on the list` } }}
						onChange={(event) => onToggleActive(voice, event.target.checked)}
					/>
				</Tooltip>

				<Tooltip title={voice.hasSample ? "Replace the sample" : "Upload a sample"}>
					<IconButton component="label" size="small" disabled={busy}>
						<UploadSimpleIcon fontSize="var(--icon-fontSize-md)" />
						{/* Clipped rather than `hidden`: MUI's ButtonBase answers Enter and Space by
						    calling the `onClick` prop, not by clicking the element, and a
						    `display: none` input cannot be reached by tab either — so `hidden`
						    leaves this control mouse-only. */}
						<input
							type="file"
							style={visuallyHidden}
							accept={VOICE_SAMPLE_ACCEPT}
							onChange={(event) => {
								const file = event.target.files?.[0];
								// Cleared before the handler runs, so choosing the same file twice still fires.
								event.target.value = "";
								if (file) onUploadSample(voice, file);
							}}
						/>
					</IconButton>
				</Tooltip>

				<Tooltip title="Edit">
					<IconButton size="small" disabled={busy} onClick={() => onEdit(voice)}>
						<PencilSimpleIcon fontSize="var(--icon-fontSize-md)" />
					</IconButton>
				</Tooltip>

				<Tooltip title="Remove">
					<IconButton color="error" size="small" disabled={busy} onClick={() => onRemove(voice)}>
						<TrashIcon fontSize="var(--icon-fontSize-md)" />
					</IconButton>
				</Tooltip>
			</Stack>
		</Stack>
	);
}
